'use client'

import Link from 'next/link'
import React from 'react'
import { Button } from '@/components/ui/button'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import type { Zimmer } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import Image from 'next/image'
import { imgproxyEnabled, imgproxyLoader } from '@/utilities/imgproxy'
import { getAltTag } from '@/utilities/getAltTag'
import { TypedLocale } from 'payload'

interface ZimmerGalleryProps {
  zimmer: Zimmer[]
  locale: TypedLocale
}

export const ZimmerGallery: React.FC<ZimmerGalleryProps> = ({ zimmer, locale }) => {
  if (!zimmer || zimmer.length === 0) {
    return <div className="py-24 text-center">Keine Zimmer gefunden.</div>
  }

  return (
    <section className="overflow-hidden py-24">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
          {zimmer.map((z) => (
            <div
              key={z.id}
              className="flex flex-col overflow-hidden bg-white shadow-md transition-transform hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="aspect-4/3 relative bg-gray-100">
                {z.heroImage ? (
                  <Image
                    loader={imgproxyEnabled ? imgproxyLoader : undefined}
                    src={getMediaUrl(z.heroImage)}
                    alt={getAltTag(z.heroImage, 'Zimmer')}
                    className="h-full w-full object-cover"
                    width={1000}
                    height={1000}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-gray-400">
                    Kein Bild
                  </div>
                )}
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="mb-2 font-serif text-xl font-light text-gray-900">{z.title}</h3>
                <div className="mb-2 text-sm text-gray-500">{z.people}</div>
                {z.description && (
                  <p className="mb-4 line-clamp-4 font-light text-gray-600">{z.description}</p>
                )}
                <div className="mt-auto">
                  <Link
                    scroll={true}
                    href={`/${locale}/${locale === 'de' ? 'zimmer' : 'rooms'}/${z.slug}`}
                    aria-label={`${locale === 'de' ? 'Mehr Infos zu' : 'More info about'} ${z.title}`}
                  >
                    <Button className="w-full bg-antonie-red text-white transition-all duration-300 hover:bg-antonie-red-hover">
                      {locale === 'de' ? 'Mehr Infos' : 'More Info'}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
